import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const doelgroepen = [
  {
    icon: 'ri-store-2-line',
    title: 'Horeca',
    subtitle: 'Restaurants, cafés & catering',
    description: 'In de horeca draait alles om marge. DUC helpt horeca-ondernemers direct in te kopen bij fabrikanten — van bedrukte koffiebekers tot verpakkingsmateriaal. Bespaar 30-80% en bouw een herkenbaar merk.',
    items: ['Bedrukte drinkbekers & lids', 'Servetten & tissues op maat', 'Bestek & servies', 'Takeaway verpakkingen', 'Branded horecamateriaal', 'Papieren & plastic bags'],
    image: 'https://public.readdy.ai/ai/img_res/0cabf37886453d489760f1c3e9bb4a2e.jpg',
    stats: [{ value: '30-80%', label: 'Besparing op inkoop' }, { value: 'DDP', label: 'All-in levering' }],
  },
  {
    icon: 'ri-shopping-cart-line',
    title: 'Webshops',
    subtitle: 'E-commerce & online retail',
    description: 'Als webshop-eigenaar is marge alles. Koop direct in bij de fabriek en verhoog je winstmarge met 30-80%. DUC regelt private label, maatwerk verpakkingen en snelle levering.',
    items: ['Private label producten', 'Maatwerk verpakkingen met logo', 'Elektronica & gadgets', 'Lifestyle & woonaccessoires', 'Sieraden & mode-accessoires', 'Seizoens- en promotieartikelen'],
    image: 'https://readdy.ai/api/search-image?query=ecommerce%20webshop%20product%20packaging%20boxes%20white%20label%20branding%20private%20label%20products%20warehouse%20shipping%20modern%20clean%20minimal%20business%20entrepreneur&width=700&height=480&seq=vw-webshop-001&orientation=landscape',
    stats: [{ value: '+40%', label: 'Hogere marge gemiddeld' }, { value: 'Laag MOQ', label: 'Ook voor kleinere webshops' }],
  },
  {
    icon: 'ri-building-4-line',
    title: 'Retail & Groothandel',
    subtitle: 'Winkels & distributeurs',
    description: 'Voor retailers en groothandels die bulk willen inkopen voor seizoensacties, eigen collecties of standaard assortiment. DUC regelt de complete supply chain.',
    items: ['Bulk seizoensproducten', 'Huishoudartikelen', 'Speelgoed & gadgets', 'Promotionele artikelen', 'Verpakkingen & displaymateriaal', 'Eigen merk & private label'],
    image: 'https://readdy.ai/api/search-image?query=retail%20store%20products%20bulk%20wholesale%20merchandise%20shelves%20modern%20clean%20minimal%20business%20professional%20supply%20chain%20products%20display%20warehouse%20logistics&width=700&height=480&seq=vw-retail-001&orientation=landscape',
    stats: [{ value: 'Bulk', label: 'Scherpe bulkprijzen' }, { value: 'Alle', label: 'Productsegmenten' }],
  },
  {
    icon: 'ri-medal-line',
    title: 'Gyms & Sportclubs',
    subtitle: 'Merchandise & sportproducten',
    description: 'Bouw een sterk merk met eigen merchandise. Shakebekers, handdoeken, tassen en sportkleding met jouw logo — DUC regelt de productie en levering.',
    items: ['Branded shakebekers', 'Handdoeken met logo', 'Sport- en gymtassen', 'Kleding & shirts op maat', 'Sportaccessoires', 'Promotiemateriaal'],
    image: 'https://readdy.ai/api/search-image?query=gym%20fitness%20sports%20merchandise%20branded%20products%20shaker%20bottles%20towels%20bags%20logo%20branding%20professional%20sports%20club%20equipment%20clean%20minimal%20modern&width=700&height=480&seq=vw-gym-001&orientation=landscape',
    stats: [{ value: 'Maatwerk', label: 'Eigen branding & logo' }, { value: 'Laag MOQ', label: 'Kleine series mogelijk' }],
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'Bedrijven & B2B',
    subtitle: 'Zakelijke inkoop op schaal',
    description: 'Of je nu bedrijfskleding, promotiemateriaal of specifieke producten nodig hebt — DUC regelt zakelijke inkoop op schaal. Met vaste contactpersoon en leveranciersmanagement.',
    items: ['Bedrijfskleding & werkkleding', 'Promotiemateriaal & relatiegeschenken', 'Kantoorbenodigdheden', 'Elektronische apparaten', 'Maatwerkproducten voor B2B', 'Leveranciersmanagement'],
    image: 'https://readdy.ai/api/search-image?query=B2B%20business%20corporate%20procurement%20professional%20office%20products%20bulk%20ordering%20supply%20chain%20management%20modern%20clean%20enterprise%20business-to-business%20industrial&width=700&height=480&seq=vw-b2b-001&orientation=landscape',
    stats: [{ value: 'Vaste', label: 'Contactpersoon' }, { value: 'Langdurig', label: 'Leveranciersrelaties' }],
  },
  {
    icon: 'ri-user-smile-line',
    title: 'Particulieren',
    subtitle: 'Geen bedrijf? Geen probleem!',
    description: 'Je hoeft echt geen bedrijf te hebben om via DUC in te kopen. Of je nu een uniek cadeau wilt laten maken of een eigen product wilt ontwikkelen — DUC helpt iedereen.',
    items: ['Persoonlijke producten op maat', 'Cadeaus met naam of logo', 'Hobby- & verzamelproducten', 'Unieke lifestyle producten', 'Kleine bestellingen mogelijk', 'Volledige begeleiding van A tot Z'],
    image: 'https://readdy.ai/api/search-image?query=individual%20consumer%20personal%20shopping%20custom%20product%20order%20online%20happy%20customer%20home%20delivery%20modern%20clean%20lifestyle%20minimal&width=700&height=480&seq=vw-particulier-001&orientation=landscape',
    stats: [{ value: 'Iedereen', label: 'Welkom bij DUC' }, { value: '100%', label: 'Begeleiding' }],
  },
];

function DoelgroepCard({ groep, index }: { groep: typeof doelgroepen[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="bg-white border border-[#0A1628]/6 hover:border-[#3B9EE8]/30 rounded-2xl overflow-hidden transition-all duration-300"
    >
      <div className={`grid lg:grid-cols-2 ${index % 2 !== 0 ? 'lg:grid-flow-col-dense' : ''}`}>
        <div className={`relative h-64 lg:h-auto overflow-hidden ${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
          <img src={groep.image} alt={groep.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A1628]/40" />
          <div className="absolute top-4 left-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm border border-white/30">
              <i className={`${groep.icon} text-white text-xl`} />
            </div>
          </div>
        </div>
        <div className={`p-8 lg:p-10 flex flex-col justify-center ${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
          <p className="text-[#3B9EE8] text-xs font-700 uppercase tracking-widest mb-2">{groep.subtitle}</p>
          <h3 className="font-heading font-800 text-[#0A1628] text-2xl lg:text-3xl mb-4">{groep.title}</h3>
          <p className="text-[#0A1628]/60 text-sm leading-relaxed mb-6">{groep.description}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
            {groep.items.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <i className="ri-check-line text-[#3B9EE8] text-sm flex-shrink-0" />
                <span className="text-[#0A1628]/65 text-xs font-500">{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-4 mb-6">
            {groep.stats.map((stat) => (
              <div key={stat.label} className="bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl px-4 py-3">
                <div className="font-heading font-800 text-[#3B9EE8] text-base">{stat.value}</div>
                <div className="text-[#0A1628]/50 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
          <a href="/#contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-6 py-3 rounded-lg text-sm font-700 transition-all duration-200 cursor-pointer whitespace-nowrap self-start">
            Vraag gratis offerte aan
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function VoorWiePage() {
  useSEO({
    title: 'Voor Wie | Horeca, Webshops, Retail & B2B Inkopen uit China | DUC',
    description: 'DUC helpt horeca, webshops, retailers en B2B-bedrijven met goedkoper inkopen uit China. Ontdek voor welke sectoren wij werken en hoe wij u 30–80% kunnen besparen.',
    keywords: 'horeca inkopen China, webshop producten importeren China, retail groothandel China, B2B inkopen leverancier China, particulieren inkopen China',
    canonical: `${SITE_URL}/voor-wie`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Voor wie', item: `${SITE_URL}/voor-wie` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Voor wie werkt DUC — Sectoren & Doelgroepen',
        description: 'DUC helpt diverse sectoren met goedkoper inkopen uit China: horeca, webshops, retail, gyms, bedrijven en particulieren.',
        url: `${SITE_URL}/voor-wie`,
        numberOfItems: 7,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Horeca', url: `${SITE_URL}/voor-wie/horeca` },
          { '@type': 'ListItem', position: 2, name: 'Webshops', url: `${SITE_URL}/voor-wie/webshops` },
          { '@type': 'ListItem', position: 3, name: 'Retail & Groothandel', url: `${SITE_URL}/voor-wie/retail` },
          { '@type': 'ListItem', position: 4, name: 'Gyms & Sportclubs', url: `${SITE_URL}/voor-wie/gyms` },
          { '@type': 'ListItem', position: 5, name: 'Bedrijven & B2B', url: `${SITE_URL}/voor-wie/bedrijven` },
          { '@type': 'ListItem', position: 6, name: 'Particulieren', url: `${SITE_URL}/voor-wie/particulieren` },
          { '@type': 'ListItem', position: 7, name: 'Juweliers', url: `${SITE_URL}/voor-wie/juweliers` },
        ],
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-14 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20entrepreneurs%20business%20owners%20professionals%20together%20working%20modern%20clean%20office%20minimal%20professional%20Dutch%20team%20collaboration&width=1920&height=600&seq=vw-hero-001&orientation=landscape"
            alt="Voor wie is DUC"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" />
              Terug naar home
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-group-line" />
            Voor wie
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            DUC werkt voor<br />
            <span className="text-[#3B9EE8]">iedereen die slimmer wil inkopen</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-lg max-w-2xl leading-relaxed">
            Van particulier tot grote retailketen. Of je nu voor het eerst inkoopt of al jarenlang actief bent — DUC helpt jou beter en goedkoper inkopen.
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col gap-8">
            {doelgroepen.map((groep, i) => (
              <DoelgroepCard key={groep.title} groep={groep} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Staat jouw sector er niet bij?
          </h2>
          <p className="text-white/55 text-lg mb-8">
            Geen probleem — DUC levert vrijwel elk product. Neem contact op voor een persoonlijk gesprek.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" />
              Gratis offerte aanvragen
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-[#3B9EE8]/60 hover:text-[#3B9EE8] px-8 py-4 rounded-lg font-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line" />
              Neem contact op
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
